import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import { useAuth } from '../../context/AuthContext'
import { subscriptionsApi } from '../../api/subscriptions'
import { getStorageUrl } from '../../utils/constants'
import Loading from '../../components/common/Loading'
import Button from '../../components/common/Button'
import { FiCalendar, FiCheckCircle, FiXCircle, FiClock, FiTrendingUp, FiChevronLeft } from 'react-icons/fi'
import './Profile.css'

const Profile = () => {
  const { user } = useAuth()

  const { data: subscriptionsData, isLoading } = useQuery({
    queryKey: ['user-subscriptions'],
    queryFn: () => subscriptionsApi.getUserSubscriptions(),
    enabled: !!user,
  })

  const subscriptions = subscriptionsData?.data?.subscriptions || []
  const now = new Date()
  
  // Find active subscription that is not expired
  const activeSubscription = subscriptions.find(sub => {
    if (sub.status !== 'active') return false
    if (!sub.expires_at) return false
    const expiresAt = new Date(sub.expires_at)
    return expiresAt > now
  })

  // Calculate days remaining for active subscription
  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return null
    const expires = new Date(expiresAt)
    const diff = expires - now
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24))
    return days
  }

  const daysRemaining = activeSubscription ? getDaysRemaining(activeSubscription.expires_at) : null
  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0
  const isExpired = daysRemaining !== null && daysRemaining <= 0

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد'
    const date = new Date(dateString)
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'غير محدد'
    const date = new Date(dateString)
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  return (
    <div className="profile-page bg-gray-900 min-h-screen">
      <Header />
      <main className="profile-main bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <h1 className="page-title text-white">الملف الشخصي</h1>
          
          {user && (
            <div className="profile-content">
              <div className="profile-card">
                <div className="profile-header">
                  {user.avatar ? (
                    <img src={getStorageUrl(user.avatar)} alt={user.name} className="profile-avatar" />
                  ) : (
                    <div className="profile-avatar-placeholder">
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <div className="profile-info">
                    <h2 className="profile-name">{user.name}</h2>
                    <p className="profile-email">{user.email}</p>
                    {user.phone && <p className="profile-phone">{user.phone}</p>}
                  </div>
                </div>
              </div>

              {/* My progress — placed above the subscription because it is
                  about the student rather than about their billing. */}
              <Link to="/profile/progress" className="profile-card profile-progress-link">
                <div className="profile-progress-link-body">
                  <FiTrendingUp className="profile-progress-link-icon" />
                  <div>
                    <h3 className="card-title">مستوايا</h3>
                    <p className="profile-progress-link-hint">
                      تقدّمك في الدورات ونتائج اختباراتك
                    </p>
                  </div>
                </div>
                <FiChevronLeft className="profile-progress-link-chevron" />
              </Link>

              {/* Subscription Status */}
              <div className="profile-card">
                <h3 className="card-title">حالة الاشتراك</h3>
                {activeSubscription && !isExpired ? (
                  <div className={`subscription-status active ${isExpiringSoon ? 'expiring-soon' : ''}`}>
                    <div className="status-header">
                      <FiCheckCircle className="status-icon active" />
                      <div>
                        <h4>اشتراك نشط</h4>
                        <p className="subscription-type">
                          {activeSubscription.package?.name || activeSubscription.course?.title || 'اشتراك في دورة'}
                        </p>
                      </div>
                    </div>
                    <div className="subscription-details">
                      {daysRemaining !== null && (
                        <div className={`detail-item days-remaining ${isExpiringSoon ? 'warning' : ''}`}>
                          <FiClock className="detail-icon" />
                          <span>
                            {daysRemaining > 0 ? (
                              <>
                                متبقي <strong>{daysRemaining}</strong> {daysRemaining === 1 ? 'يوم' : 'أيام'}
                              </>
                            ) : (
                              'منتهي'
                            )}
                          </span>
                        </div>
                      )}
                      <div className="detail-item">
                        <FiCalendar className="detail-icon" />
                        <span>ينتهي في: {formatDateTime(activeSubscription.expires_at)}</span>
                      </div>
                      {activeSubscription.package && (
                        <div className="detail-item">
                          <span>المدة: {activeSubscription.package.duration_days} يوم</span>
                        </div>
                      )}
                      {isExpiringSoon && (
                        <div className="expiration-warning">
                          <FiClock className="warning-icon" />
                          <span>سيتم انتهاء الاشتراك قريباً! تجديد الاشتراك للاستمرار في الوصول</span>
                        </div>
                      )}
                    </div>
                    {isExpiringSoon && (
                      <Link to="/subscriptions" style={{ marginTop: '16px', display: 'block' }}>
                        <Button variant="primary" fullWidth>
                          تجديد الاشتراك
                        </Button>
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="subscription-status inactive">
                    <div className="status-header">
                      <FiXCircle className="status-icon inactive" />
                      <div>
                        <h4>لا يوجد اشتراك نشط</h4>
                        <p>
                          {isExpired ? 'انتهى الاشتراك. اشترك الآن للوصول إلى جميع المحتوى' : 'اشترك الآن للوصول إلى جميع المحتوى'}
                        </p>
                      </div>
                    </div>
                    <Link to="/subscriptions">
                      <Button variant="primary" fullWidth>
                        اشترك الآن
                      </Button>
                    </Link>
                  </div>
                )}

                {subscriptions.length > 0 && (
                  <div className="subscriptions-history">
                    <h4>سجل الاشتراكات</h4>
                    <div className="subscriptions-list">
                      {subscriptions.map((sub) => {
                        const subExpiresAt = sub.expires_at ? new Date(sub.expires_at) : null
                        const isSubExpired = subExpiresAt && subExpiresAt <= now
                        const subDaysRemaining = subExpiresAt ? getDaysRemaining(sub.expires_at) : null
                        const actualStatus = isSubExpired ? 'expired' : (sub.status === 'active' ? 'active' : sub.status)
                        
                        return (
                          <div key={sub.id} className={`subscription-item ${actualStatus}`}>
                            <div className="subscription-item-header">
                              <span className="subscription-item-name">
                                {sub.package?.name || sub.course?.title || 'اشتراك'}
                              </span>
                              <span className={`subscription-item-status ${actualStatus}`}>
                                {actualStatus === 'active' 
                                  ? (subDaysRemaining !== null && subDaysRemaining > 0 
                                      ? `نشط (${subDaysRemaining} ${subDaysRemaining === 1 ? 'يوم' : 'أيام'})` 
                                      : 'نشط')
                                  : actualStatus === 'expired' 
                                    ? 'منتهي' 
                                    : 'ملغي'}
                              </span>
                            </div>
                            <div className="subscription-item-details">
                              <span>من: {formatDate(sub.created_at)}</span>
                              <span>إلى: {formatDateTime(sub.expires_at)}</span>
                              {subDaysRemaining !== null && subDaysRemaining > 0 && actualStatus === 'active' && (
                                <span className="subscription-item-days">
                                  متبقي {subDaysRemaining} {subDaysRemaining === 1 ? 'يوم' : 'أيام'}
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="profile-card">
                <h3 className="card-title">إعدادات سريعة</h3>
                <div className="quick-actions">
                  <Link to="/my-courses" className="action-link">
                    دوراتي
                  </Link>
                  <Link to="/favorites" className="action-link">
                    المفضلة
                  </Link>
                  <Link to="/notes" className="action-link">
                    ملاحظاتي
                  </Link>
                  <Link to="/exams" className="action-link">
                    الاختبارات
                  </Link>
                  <Link to="/notifications" className="action-link">
                    الإشعارات
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Profile

